import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  message,
  Modal,
  Tag,
  Form,
  Select,
  Pagination,
} from 'antd';
import {
  ToolOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { aftersalesApi, authApi } from '@/services/api';
import type {
  AftersalesOrder,
  AftersalesWorkOrderStatus,
  AftersalesServiceType,
  CreateAftersalesRequest,
} from '@/types';

const WORK_ORDER_STATUS_LABEL: Record<AftersalesWorkOrderStatus, string> = {
  created: '待处理',
  pending_confirmation: '待客户确认',
  closed: '已完成',
  rejected: '已退回',
};

const WORK_ORDER_STATUS_COLOR: Record<AftersalesWorkOrderStatus, string> = {
  created: 'default',
  pending_confirmation: 'processing',
  closed: 'success',
  rejected: 'warning',
};

const SERVICE_TYPE_LABEL: Record<AftersalesServiceType, string> = {
  software: '软件故障',
  hardware: '硬件故障',
  maintenance: '售后维保',
};

function AftersalesPage() {
  const navigate = useNavigate();
  const currentUser = authApi.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [orders, setOrders] = useState<AftersalesOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [workOrderStatus, setWorkOrderStatus] = useState<AftersalesWorkOrderStatus | undefined>();
  const [serviceType, setServiceType] = useState<AftersalesServiceType | undefined>();
  const [mineOnly, setMineOnly] = useState(false);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm<CreateAftersalesRequest>();

  const loadOrders = async () => {
    setLoading(true);
    try {
      const result = await aftersalesApi.list({
        page,
        limit,
        search: search || undefined,
        workOrderStatus,
        serviceType,
        mine: isAdmin ? mineOnly : undefined,
      });
      setOrders(result.data || []);
      setTotal(result.pagination?.total || 0);
    } catch (err: any) {
      message.error(err?.response?.data?.message || err.message || '加载工单列表失败');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, workOrderStatus, serviceType, mineOnly]);

  const handleCreate = async (values: CreateAftersalesRequest) => {
    setCreating(true);
    try {
      const order = await aftersalesApi.create(values);
      message.success(`工单创建成功：${order.serialNumber}`);
      setCreateModalVisible(false);
      createForm.resetFields();
      navigate(`/admin/aftersales/${order.serialNumber}`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || err.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (order: AftersalesOrder) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除工单 ${order.serialNumber} 吗？此操作不可恢复！`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await aftersalesApi.delete(order.serialNumber);
          message.success('删除成功');
          loadOrders();
        } catch (err: any) {
          message.error(err?.response?.data?.message || err.message || '删除失败');
        }
      },
    });
  };

  const columns = [
    {
      title: '工单号',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 180,
      render: (sn: string) => (
        <span style={{ fontFamily: 'monospace', color: '#165DFF' }}>{sn}</span>
      ),
    },
    {
      title: '公司名称',
      dataIndex: 'companyName',
      key: 'companyName',
    },
    {
      title: '企业联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 100,
    },
    {
      title: '服务类型',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 100,
      render: (type: AftersalesServiceType) => SERVICE_TYPE_LABEL[type] || type,
    },
    {
      title: '处理人',
      key: 'technician',
      width: 120,
      render: (_: any, record: AftersalesOrder) => record.technician?.name || '-',
    },
    {
      title: '工单状态',
      dataIndex: 'workOrderStatus',
      key: 'workOrderStatus',
      width: 130,
      render: (status: AftersalesWorkOrderStatus, record: AftersalesOrder) => (
        <Space size={4}>
          <Tag color={WORK_ORDER_STATUS_COLOR[status]}>
            {WORK_ORDER_STATUS_LABEL[status]}
          </Tag>
          {record.rejectCount > 0 && (
            <Tag color="orange" style={{ marginLeft: 0 }}>
              退回 {record.rejectCount} 次
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_: any, record: AftersalesOrder) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/aftersales/${record.serialNumber}`)}
          >
            详情
          </Button>
          {isAdmin && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <ToolOutlined />
            <span>售后工单</span>
          </Space>
        }
        extra={isAdmin ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            新建工单
          </Button>
        ) : null}
      >
        <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="搜索工单号/公司/企业联系人"
            allowClear
            style={{ width: 260 }}
            onSearch={(v) => {
              setPage(1);
              setSearch(v);
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setPage(1);
                setSearch('');
              }
            }}
          />
          <Select
            placeholder="工单状态"
            allowClear
            style={{ width: 160 }}
            value={workOrderStatus}
            onChange={(v) => {
              setPage(1);
              setWorkOrderStatus(v);
            }}
            options={(Object.keys(WORK_ORDER_STATUS_LABEL) as AftersalesWorkOrderStatus[]).map(
              (k) => ({ value: k, label: WORK_ORDER_STATUS_LABEL[k] })
            )}
          />
          <Select
            placeholder="服务类型"
            allowClear
            style={{ width: 130 }}
            value={serviceType}
            onChange={(v) => {
              setPage(1);
              setServiceType(v);
            }}
            options={(Object.keys(SERVICE_TYPE_LABEL) as AftersalesServiceType[]).map((k) => ({
              value: k,
              label: SERVICE_TYPE_LABEL[k],
            }))}
          />
          {isAdmin && (
            <Button
              type={mineOnly ? 'primary' : 'default'}
              onClick={() => {
                setPage(1);
                setMineOnly(!mineOnly);
              }}
            >
              {mineOnly ? '查看全部' : '我负责的'}
            </Button>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={orders}
          rowKey="serialNumber"
          loading={loading}
          pagination={false}
        />
        <div style={{ marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={(newPage, newLimit) => {
              setPage(newPage);
              setLimit(newLimit);
            }}
            showSizeChanger
            showTotal={(t) => `共计 ${t} 条记录`}
          />
        </div>
      </Card>

      <Modal
        title="新建售后工单"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={560}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="companyName"
            label="公司名称"
            rules={[{ required: true, message: '请输入公司名称' }]}
          >
            <Input placeholder="例如：浙江北凡科技" />
          </Form.Item>
          <Form.Item
            name="companyAddress"
            label="公司位置"
            rules={[{ required: true, message: '请输入公司位置' }]}
          >
            <Input placeholder="例如：杭州市西湖区文三路 100 号" />
          </Form.Item>
          <Form.Item
            name="contactName"
            label="企业联系人"
            rules={[{ required: true, message: '请输入企业联系人' }]}
          >
            <Input placeholder="企业联系人姓名" />
          </Form.Item>
          <Form.Item
            name="contactPhone"
            label="联系电话"
            rules={[
              { required: true, message: '请输入联系电话' },
              { pattern: /^\d{11}$/, message: '请输入 11 位手机号' },
            ]}
          >
            <Input placeholder="11 位手机号" maxLength={11} />
          </Form.Item>
          <Form.Item
            name="serviceType"
            label="服务类型"
            rules={[{ required: true, message: '请选择服务类型' }]}
          >
            <Select
              placeholder="请选择"
              options={(Object.keys(SERVICE_TYPE_LABEL) as AftersalesServiceType[]).map((k) => ({
                value: k,
                label: SERVICE_TYPE_LABEL[k],
              }))}
            />
          </Form.Item>
          <Form.Item
            name="issueDescription"
            label="现场情况说明"
            rules={[{ required: true, message: '请填写现场情况说明' }]}
          >
            <Input.TextArea rows={4} placeholder="请描述客户反馈的问题" />
          </Form.Item>
          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setCreateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default AftersalesPage;
